/**
 * Service Desk client for The Sweet Spot: the data contract the shared
 * `HelpCenter` needs, wired to `/v1/servicedesk/...` on the shared API.
 * Every route answers `{ data: ... }`, unwrapped here.
 *
 * Store logins ask the server for their own store's people list. A corporate
 * user has no store of their own, so `getStoreNo` supplies the store picked in
 * the header and it rides along as `store_no`.
 */

import type {
  SdArticle,
  SdArticleSummary,
  SdAsset,
  SdAttachment,
  SdCategoryNode,
  SdComment,
  SdCreateTicketInput,
  SdFormField,
  SdPerson,
  SdReplyInput,
  SdStore,
  SdTicketDetail,
  SdTicketSummary,
  ServiceDeskClient,
} from '@ampersandbrands/ui';
import { apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';
import { uploadEditorImage } from './editorImages';

const SD = `${API_BASE}/v1/servicedesk`;
const DESK = 'it';

async function get<T>(path: string): Promise<T> {
  return (await apiGet<{ data: T }>(`${SD}${path}`)).data;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return (await apiPost<{ data: T }>(`${SD}${path}`, body)).data;
}

/** Attachment URLs come back host-relative; the app runs on another host. */
function absolutize(a: SdAttachment): SdAttachment {
  return a.url.startsWith('/') ? { ...a, url: `${API_BASE}${a.url}` } : a;
}

function fixComment(c: SdComment): SdComment {
  return { ...c, body_html: fixHtml(c.body_html), attachments: (c.attachments ?? []).map(absolutize) };
}

function fixTicket(t: SdTicketDetail): SdTicketDetail {
  return {
    ...t,
    description_html: t.description_html ? fixHtml(t.description_html) : t.description_html,
    attachments: (t.attachments ?? []).map(absolutize),
    comments: (t.comments ?? []).map(fixComment),
  };
}

/**
 * Images inside article, description and comment HTML are root-relative paths on the API:
 * `/v1/core/images/{id}` (editor uploads) and `/v1/servicedesk/attachments/{id}` (imported
 * screenshots). The first replace covers every `src="/v1/servicedesk/` path, attachments included.
 */
function fixHtml(html: string): string {
  return html
    .replaceAll('src="/v1/servicedesk/', `src="${API_BASE}/v1/servicedesk/`)
    .replace(/(<img\b[^>]*?\bsrc=")(\/v1\/core\/images\/\d+)(")/gi, `$1${API_BASE}$2$3`);
}

export function createServiceDeskClient(
  getStoreNo?: () => string | number | null | undefined,
): ServiceDeskClient {
  const storeNo = () => {
    const v = getStoreNo?.();
    return v === null || v === undefined || v === '' ? undefined : String(v);
  };

  return {
    uploadImage: uploadEditorImage,
    getCategoryTree: () => get<SdCategoryNode[]>(`/desks/${DESK}/categories`),
    getCategoryForPage: (pageKey) =>
      get<Awaited<ReturnType<NonNullable<ServiceDeskClient['getCategoryForPage']>>>>(
        `/desks/${DESK}/categories/for-page?key=${encodeURIComponent(pageKey)}`,
      ),
    getForm: (categoryId) => get<SdFormField[]>(`/desks/${DESK}/form?category_id=${categoryId}`),
    listPeople: (scope, query) => {
      const p = new URLSearchParams({ scope });
      if (query) p.set('q', query);
      const s = storeNo();
      if (s) p.set('store_no', s);
      return get<SdPerson[]>(`/people?${p.toString()}`);
    },
    listStores: () => get<SdStore[]>('/stores'),
    getPersonAssets: (email) => get<SdAsset[]>(`/people/${encodeURIComponent(email)}/assets`),
    createTicket: (input: SdCreateTicketInput) => {
      const s = input.store_no ?? storeNo();
      return post<SdTicketSummary>('/tickets', { ...input, ...(s ? { store_no: s } : {}), desk: DESK });
    },
    completeDetails: (ticketId, input) => post<SdTicketSummary>(`/tickets/${ticketId}/details`, input),
    listMyTickets: (filter) => get<SdTicketSummary[]>(`/me/tickets?filter=${filter}`),
    getTicket: async (ticketId) => fixTicket(await get<SdTicketDetail>(`/tickets/${ticketId}`)),
    addReply: async (ticketId, input: SdReplyInput) =>
      fixComment(await post<SdComment>(`/tickets/${ticketId}/replies`, input)),
    confirmResolved: async (ticketId, fixed, note) =>
      fixTicket(await post<SdTicketDetail>(`/tickets/${ticketId}/confirm`, { fixed, note })),
    markRead: async (ticketId) => {
      await post(`/tickets/${ticketId}/read`, {});
    },
    getUnreadCount: async () => (await get<{ count: number }>('/me/unread')).count,
    listArticles: () => get<SdArticleSummary[]>('/articles'),
    searchArticles: (query, categoryId) =>
      get<SdArticleSummary[]>(
        `/articles?q=${encodeURIComponent(query)}${categoryId ? `&category_id=${categoryId}` : ''}`,
      ),
    getArticle: async (articleId) => {
      const a = await get<SdArticle>(`/articles/${articleId}`);
      return { ...a, html: fixHtml(a.html) };
    },
    recordArticleView: async (articleId) => {
      await post(`/articles/${articleId}/view`, {});
    },
  };
}
